import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Section } from "@/components/Section";
import { CircleOfFifths } from "@/components/CircleOfFifths";
import { Notation, NotationMeasure } from "@/components/Notation";
import { Transport } from "@/components/Transport";
import {
  CIRCLE_OF_FIFTHS,
  circleIndex,
  circleLabel,
  diatonicTriads,
  triadPcs,
  voicing,
} from "@/theory/notes";
import { playNow, playSequence } from "@/audio/piano";
import { startMetronome, stopMetronome, setMetronomeBpm } from "@/audio/metronome";

const ACCENT = "#f59e0b"; // amber-500
const BLUE = "#3b82f6";
const RED = "#ef4444";

// Enharmonic-tolerant, so "Bb" and "A#" resolve to the same seat.
const fifthsIndex = circleIndex;

const COURSE_LABEL = "Music Theory · Lesson 11";
const LESSON_TITLE = "Circle of Fifths for Triads";
const ORIGINAL_VIDEO = "https://learn-music.nathanielschool.com/learn/music-theory";

/* ---------- small helpers used by the lesson ---------- */

function ChordButton({
  label,
  pcs,
  color,
  onPlay,
}: {
  label: string;
  pcs: string[];
  color: string;
  onPlay: () => void;
}) {
  return (
    <button
      onClick={onPlay}
      className="group flex flex-col items-start gap-1 rounded-xl border border-nsm-border bg-nsm-card/70 p-3 transition-all hover:border-amber-500/60 hover:bg-nsm-card"
      style={{ boxShadow: `inset 0 0 0 1px ${color}22` }}
    >
      <div className="flex w-full items-center justify-between">
        <span className="font-display text-base font-bold text-nsm-ink">{label}</span>
        <span
          className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
          style={{ background: `${color}22`, color }}
        >
          play
        </span>
      </div>
      <span className="text-xs text-nsm-dim">{pcs.join("  ·  ")}</span>
    </button>
  );
}

/* ============================================================
 *  LESSON
 * ============================================================ */

export default function CircleOfFifthsTriads() {
  const [tonic, setTonic] = useState("C");
  const [bpm, setBpm] = useState(80);
  const [playing, setPlaying] = useState<string | null>(null); // section id playing
  const [metroOn, setMetroOn] = useState(false);
  const [highlightedDegree, setHighlightedDegree] = useState<number | null>(null);

  const stopRef = useRef<(() => void) | null>(null);

  useEffect(() => () => stopRef.current?.(), []);

  const triads = useMemo(() => diatonicTriads(tonic), [tonic]);
  const tonicIdx = fifthsIndex(tonic);

  // Indexes around the circle relative to current tonic
  const idxFor = (pc: string) => fifthsIndex(pc);

  /* Helpers -------------------------------------------------- */

  const playChord = async (pcs: string[], dur = "2n") => {
    await playNow(voicing(pcs, 4), dur);
  };

  const playProgression = async (id: string, events: { pcs: string[]; beats: number }[]) => {
    stopRef.current?.();
    setPlaying(id);
    let cursorBeats = 0;
    const seq = events.map((e) => {
      const time = `${Math.floor(cursorBeats / 4)}:${cursorBeats % 4}:0`;
      cursorBeats += e.beats;
      return {
        time,
        notes: voicing(e.pcs, 4),
        duration: `${e.beats}n` as string,
      };
    });
    const totalSeconds = (cursorBeats / bpm) * 60 + 0.6;
    const cleanup = await playSequence(seq, bpm);
    stopRef.current = cleanup;
    window.setTimeout(() => {
      cleanup();
      stopRef.current = null;
      setPlaying((cur) => (cur === id ? null : cur));
    }, totalSeconds * 1000);
  };

  const stopAll = () => {
    stopRef.current?.();
    stopRef.current = null;
    setPlaying(null);
  };

  /* Highlights on the wheel for the *current section* ------- */
  // We compute the most useful default — major trio in current key.
  const majorTrioHighlights = [
    { index: idxFor(triads[0].pcs[0]), color: ACCENT, label: "I" },
    { index: idxFor(triads[3].pcs[0]), color: ACCENT, label: "IV" },
    { index: idxFor(triads[4].pcs[0]), color: ACCENT, label: "V" },
  ];
  const minorClusterHighlights = [
    { index: idxFor(triads[1].pcs[0]), color: BLUE, label: "ii" },
    { index: idxFor(triads[5].pcs[0]), color: BLUE, label: "vi" },
    { index: idxFor(triads[2].pcs[0]), color: BLUE, label: "iii" },
  ];
  const dimHighlight = [{ index: idxFor(triads[6].pcs[0]), color: RED, label: "vii°" }];

  const allDiatonic = [...majorTrioHighlights, ...minorClusterHighlights, ...dimHighlight];

  /* Per-section metronome control --------------------------- */
  const handleToggleMetronome = async () => {
    if (metroOn) {
      stopMetronome();
      setMetroOn(false);
    } else {
      // onStopped fires if a chord button later takes the transport, so the
      // toggle can never sit lit over silence.
      await startMetronome({ bpm, beatsPerBar: 4, onStopped: () => setMetroOn(false) });
      setMetroOn(true);
    }
  };
  useEffect(() => {
    if (metroOn) setMetronomeBpm(bpm);
  }, [bpm, metroOn]);

  /* ---------------------------------------------------------- */

  // --- Cadence notation measures -----------------------------
  const cadenceMeasures = (kind: "plagal" | "authentic"): NotationMeasure[] => {
    const I = voicing(triads[0].pcs, 4);
    const IV = voicing(triads[3].pcs, 4);
    const V = voicing(triads[4].pcs, 4);
    if (kind === "plagal") {
      return [
        { label: "IV", chords: [{ keys: IV, duration: "h" }, { keys: IV, duration: "h" }] },
        { label: "I",  chords: [{ keys: I, duration: "h" }, { keys: I, duration: "h" }] },
      ];
    }
    return [
      { label: "V", chords: [{ keys: V, duration: "h" }, { keys: V, duration: "h" }] },
      { label: "I", chords: [{ keys: I, duration: "h" }, { keys: I, duration: "h" }] },
    ];
  };

  // Diatonic seven-chord measure for "all chords" view
  const diatonicMeasures: NotationMeasure[] = triads.map((t) => ({
    label: t.roman,
    chords: [{ keys: voicing(t.pcs, 4), duration: "w" }],
  }));

  /* ============================================================
   *  RENDER
   * ============================================================ */
  return (
    <div className="min-h-screen pb-12">
      <Header course={COURSE_LABEL} lesson={LESSON_TITLE} videoUrl={ORIGINAL_VIDEO} />

      {/* HERO -------------------------------------------------- */}
      <section className="relative overflow-hidden border-b border-nsm-border">
        <div
          aria-hidden
          className="absolute inset-0 -z-10 opacity-50"
          style={{
            background:
              "radial-gradient(700px 360px at 30% 30%, rgba(245,158,11,0.18), transparent 60%), radial-gradient(700px 360px at 80% 70%, rgba(59,130,246,0.12), transparent 60%)",
          }}
        />
        <div className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-10 px-4 py-12 lg:grid-cols-2 lg:px-8 lg:py-20">
          <div>
            <div className="section-eyebrow">Interactive companion</div>
            <h1 className="mt-2 font-display text-4xl font-bold leading-tight text-nsm-ink lg:text-5xl">
              The Circle of Fifths,<br />
              <span className="text-amber-400">Made of Triads</span>
            </h1>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-nsm-dim">
              we call the Circle a map. This page turns the map into a keyboard. Pick a key,
              click any chord, and watch the diatonic family light up — three majors clustered on
              one side, three minors on the other, and a single diminished chord stuck out at the
              far edge.
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <span className="nsm-chip"><span className="text-amber-400">Key</span> · pick below</span>
              <span className="nsm-chip">Real grand piano playback</span>
              <span className="nsm-chip">Live notation</span>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-2">
              <span className="text-xs uppercase tracking-wider text-nsm-dim">Tonic</span>
              {["C", "G", "D", "A", "E", "F", "Bb", "Eb"].map((k) => (
                <button
                  key={k}
                  onClick={() => setTonic(k)}
                  className={`rounded-md border px-2.5 py-1 text-sm font-semibold transition ${
                    tonicIdx === fifthsIndex(k)
                      ? "border-amber-500 bg-amber-500/15 text-amber-300"
                      : "border-nsm-border bg-nsm-surface text-nsm-dim hover:bg-nsm-card"
                  }`}
                >
                  {k}
                </button>
              ))}
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <CircleOfFifths
              size={380}
              rootIndex={tonicIdx}
              highlights={allDiatonic}
              onSelect={(pc) => setTonic(pc)}
            />
          </div>
        </div>
      </section>

      {/* WHY -------------------------------------------------- */}
      <Section eyebrow="Why this matters" title="Two facts that unlock every key">
        <p>
          A major scale is built from <strong>seven notes</strong>; on each note sits a triad. There
          are only ever <strong>three majors, three minors, and one diminished</strong> — the
          recipe never changes. The Circle of Fifths just rearranges those seven into a shape your
          eye can read instantly: the three majors huddle together, the three minors huddle right
          next to them, and the diminished sits alone at the far edge.
        </p>
        <p>
          That means once you know the trick, you don't memorise chords for twelve keys. You
          memorise <strong>one shape</strong> — and slide it around the wheel.
        </p>
      </Section>

      {/* MAJOR TRIO ------------------------------------------- */}
      <Section
        eyebrow="The trio"
        title={`I, IV and V — the three majors of ${circleLabel(tonicIdx)} major`}
      >
        <p>
          Around your tonic, the <strong>clockwise</strong> neighbour is the <strong>V</strong>{" "}
          (a perfect fifth up) and the <strong>counter-clockwise</strong> neighbour is the{" "}
          <strong>IV</strong> (a perfect fourth up, or fifth down). Three squares — three majors.
          That tiny cluster also hands you both classical cadences for free.
        </p>

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
          {/* min-w-0: a grid column grows to fit its widest child unless told not to,
              so the 720px notation made the whole page scroll sideways on a phone
              instead of scrolling inside its own frame. */}
          <div className="min-w-0 space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <ChordButton
                label={`${triads[0].pcs[0]} maj  ·  I`}
                pcs={triads[0].pcs}
                color={ACCENT}
                onPlay={() => playChord(triads[0].pcs)}
              />
              <ChordButton
                label={`${triads[3].pcs[0]} maj  ·  IV`}
                pcs={triads[3].pcs}
                color={ACCENT}
                onPlay={() => playChord(triads[3].pcs)}
              />
              <ChordButton
                label={`${triads[4].pcs[0]} maj  ·  V`}
                pcs={triads[4].pcs}
                color={ACCENT}
                onPlay={() => playChord(triads[4].pcs)}
              />
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <button
                onClick={() =>
                  playProgression("plagal", [
                    { pcs: triads[3].pcs, beats: 4 },
                    { pcs: triads[0].pcs, beats: 4 },
                  ])
                }
                className="nsm-btn-ghost h-auto items-start py-3 text-left"
              >
                <div>
                  <div className="font-semibold text-nsm-ink">Plagal cadence — IV → I</div>
                  <div className="text-xs text-nsm-dim">"Amen". Counter-clockwise on the circle.</div>
                </div>
              </button>
              <button
                onClick={() =>
                  playProgression("auth", [
                    { pcs: triads[4].pcs, beats: 4 },
                    { pcs: triads[0].pcs, beats: 4 },
                  ])
                }
                className="nsm-btn-ghost h-auto items-start py-3 text-left"
              >
                <div>
                  <div className="font-semibold text-nsm-ink">Authentic cadence — V → I</div>
                  <div className="text-xs text-nsm-dim">The "home" pull. Clockwise resolves to root.</div>
                </div>
              </button>
            </div>

            <div className="rounded-xl border border-nsm-border bg-nsm-card/40 p-3">
              <div className="mb-2 flex items-center justify-between text-xs uppercase tracking-wider text-nsm-dim">
                <span>Notation · Plagal then Authentic</span>
                <span>Treble · 4/4</span>
              </div>
              <Notation
                measures={[...cadenceMeasures("plagal"), ...cadenceMeasures("authentic")]}
                width={680}
                height={150}
              />
            </div>
          </div>

          <div className="flex flex-col items-center gap-3">
            <CircleOfFifths
              size={300}
              rootIndex={tonicIdx}
              highlights={majorTrioHighlights}
              onSelect={(pc) => setTonic(pc)}
            />
            <div className="text-center text-xs text-nsm-dim">
              Three squares around the tonic = the three major chords of the key.
            </div>
          </div>
        </div>
      </Section>

      {/* MINOR CLUSTER --------------------------------------- */}
      <Section eyebrow="The next arc" title="ii, iii, vi — the three minors, all in one curve">
        <p>
          Keep walking <strong>clockwise</strong> past V and you land on the next three slots —
          which are the <strong>minor</strong> chords of the key. In C major they are{" "}
          <strong>D minor (ii), A minor (vi), E minor (iii)</strong>. Same shape, same trick — and
          this is where one of pop's most familiar progressions lives:{" "}
          <strong>I – V – vi – IV</strong>.
        </p>
        <div className="mt-4 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <ChordButton
                label={`${triads[1].pcs[0]} min  ·  ii`}
                pcs={triads[1].pcs}
                color={BLUE}
                onPlay={() => playChord(triads[1].pcs)}
              />
              <ChordButton
                label={`${triads[5].pcs[0]} min  ·  vi`}
                pcs={triads[5].pcs}
                color={BLUE}
                onPlay={() => playChord(triads[5].pcs)}
              />
              <ChordButton
                label={`${triads[2].pcs[0]} min  ·  iii`}
                pcs={triads[2].pcs}
                color={BLUE}
                onPlay={() => playChord(triads[2].pcs)}
              />
            </div>
            <button
              onClick={() =>
                playProgression("pop", [
                  { pcs: triads[0].pcs, beats: 4 },
                  { pcs: triads[4].pcs, beats: 4 },
                  { pcs: triads[5].pcs, beats: 4 },
                  { pcs: triads[3].pcs, beats: 4 },
                ])
              }
              className="nsm-btn-primary"
            >
              Play I – V – vi – IV
            </button>
          </div>
          <CircleOfFifths
            size={300}
            rootIndex={tonicIdx}
            highlights={[...majorTrioHighlights, ...minorClusterHighlights]}
            onSelect={(pc) => setTonic(pc)}
          />
        </div>
      </Section>

      {/* DIMINISHED ------------------------------------------ */}
      <Section eyebrow="The outlier" title="vii° — one chord, sitting alone">
        <p>
          The seventh degree is a <strong>diminished triad</strong> — built from a root, a minor
          third, and a tritone (the famously unstable "augmented fourth"). On the circle it sits at
          the <strong>opposite end</strong> from the major trio. In C, that's{" "}
          <strong>B diminished</strong> — and the tritone <em>B → F</em> is exactly what gives it
          its restless, "needs-to-resolve" sound.
        </p>
        <div className="mt-4 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
          <div className="space-y-4">
            <ChordButton
              label={`${triads[6].pcs[0]} dim  ·  vii°`}
              pcs={triads[6].pcs}
              color={RED}
              onPlay={() => playChord(triads[6].pcs)}
            />
            <button
              onClick={() =>
                playProgression("dim-resolve", [
                  { pcs: triads[6].pcs, beats: 4 },
                  { pcs: triads[0].pcs, beats: 4 },
                ])
              }
              className="nsm-btn-ghost"
            >
              Hear it resolve · vii° → I
            </button>
          </div>
          <CircleOfFifths
            size={300}
            rootIndex={tonicIdx}
            highlights={[...allDiatonic]}
            onSelect={(pc) => setTonic(pc)}
          />
        </div>
      </Section>

      {/* ALL DIATONIC SEVEN ---------------------------------- */}
      <Section eyebrow="All seven, side by side" title={`Every triad in ${circleLabel(tonicIdx)} major`}>
        <p>
          Step through the whole diatonic family. Click any chord on the wheel below to scrub the
          highlight; click any roman numeral on the staff to hear it.
        </p>
        <div className="mt-4 nsm-card p-3">
          <div className="mb-2 flex items-center justify-between text-xs uppercase tracking-wider text-nsm-dim">
            <span>Diatonic triads · {circleLabel(tonicIdx)} major</span>
            <span>I · ii · iii · IV · V · vi · vii°</span>
          </div>
          <Notation
            measures={diatonicMeasures}
            width={840}
            height={150}
            highlightIndex={highlightedDegree ?? undefined}
          />
          <div className="mt-3 grid grid-cols-7 gap-2">
            {triads.map((t, i) => (
              <button
                key={t.roman}
                onMouseEnter={() => setHighlightedDegree(i)}
                onMouseLeave={() => setHighlightedDegree(null)}
                onClick={() => playChord(t.pcs)}
                className="rounded-md border border-nsm-border bg-nsm-surface py-2 text-center hover:bg-nsm-card"
              >
                <div className="font-display text-sm font-semibold text-nsm-ink">{t.roman}</div>
                <div className="text-[11px] text-nsm-dim">{t.pcs[0]} {t.quality === "major" ? "" : t.quality === "minor" ? "m" : "°"}</div>
              </button>
            ))}
          </div>
        </div>
      </Section>

      {/* BORROWED CHORDS ------------------------------------- */}
      <Section eyebrow="Going parallel" title="Borrowing from the parallel minor">
        <p>
          Walk <strong>counter-clockwise</strong> from the tonic and you slide into the world of
          the <em>parallel minor</em>. The trio of majors (I, IV, V) flips to minors, and three new
          major chords emerge: <strong>♭III, ♭VI, ♭VII</strong>. These are the "borrowed chords"
          that make pop, gospel and film music sound emotionally rich.
        </p>
        <div className="mt-4 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            {[
              { roman: "♭III", pcs: triadPcs(CIRCLE_OF_FIFTHS[(tonicIdx + 9) % 12], "major"), color: "#8b5cf6" },
              { roman: "♭VI", pcs: triadPcs(CIRCLE_OF_FIFTHS[(tonicIdx + 8) % 12], "major"), color: "#8b5cf6" },
              { roman: "♭VII", pcs: triadPcs(CIRCLE_OF_FIFTHS[(tonicIdx + 10) % 12], "major"), color: "#8b5cf6" },
              { roman: "i", pcs: triadPcs(CIRCLE_OF_FIFTHS[tonicIdx], "minor"), color: "#a78bfa" },
              { roman: "iv", pcs: triadPcs(CIRCLE_OF_FIFTHS[(tonicIdx + 11) % 12], "minor"), color: "#a78bfa" },
              { roman: "v", pcs: triadPcs(CIRCLE_OF_FIFTHS[(tonicIdx + 1) % 12], "minor"), color: "#a78bfa" },
            ].map((c) => (
              <ChordButton
                key={c.roman}
                label={`${c.roman}  ·  ${c.pcs[0]}`}
                pcs={c.pcs}
                color={c.color}
                onPlay={() => playChord(c.pcs)}
              />
            ))}
          </div>
          <CircleOfFifths
            size={300}
            rootIndex={tonicIdx}
            highlights={[
              { index: tonicIdx, color: "#a78bfa", label: "i" },
              { index: (tonicIdx + 11) % 12, color: "#a78bfa", label: "iv" },
              { index: (tonicIdx + 1) % 12, color: "#a78bfa", label: "v" },
              { index: (tonicIdx + 9) % 12, color: "#8b5cf6", label: "♭III" },
              { index: (tonicIdx + 8) % 12, color: "#8b5cf6", label: "♭VI" },
              { index: (tonicIdx + 10) % 12, color: "#8b5cf6", label: "♭VII" },
            ]}
            onSelect={(pc) => setTonic(pc)}
          />
        </div>
      </Section>

      {/* PRACTICE WITH METRONOME ---------------------------- */}
      <Section eyebrow="Practice room" title="I – IV – V – I in time">
        <p>
          Lock the metronome on, set a tempo you can sing through, and cycle the cadence both ways.
          The audio sample is a real Salamander Grand — same instrument used for film scoring — so
          you'll hear honest voicing balance.
        </p>
        <Transport
          bpm={bpm}
          onBpmChange={setBpm}
          isPlaying={!!playing}
          onPlay={() =>
            playProgression("practice", [
              { pcs: triads[0].pcs, beats: 4 },
              { pcs: triads[3].pcs, beats: 4 },
              { pcs: triads[4].pcs, beats: 4 },
              { pcs: triads[0].pcs, beats: 4 },
            ])
          }
          onStop={stopAll}
          metronomeOn={metroOn}
          onToggleMetronome={handleToggleMetronome}
          presets={[
            { label: "Slow", bpm: 60 },
            { label: "Medium", bpm: 80 },
            { label: "Up", bpm: 110 },
          ]}
        />
        <div className="mt-3 text-xs text-nsm-dim">
          Tip: practise singing the root of each chord on beat 1 ("Doh, Fa, So, Doh"). Once that
          locks, try walking the bass line in your left hand while voicing the triad in your right.
        </div>
      </Section>

      {/* RECAP ------------------------------------------------ */}
      <Section eyebrow="Recap" title="What you can now do without thinking">
        <ul className="space-y-3">
          {[
            "Find the I, IV and V of any major key by glancing at the wheel — they cluster around the tonic.",
            "Spot the three minors (ii, iii, vi) as the next clockwise arc.",
            "Locate the lone diminished chord at the far edge — and know the tritone is what makes it itch.",
            "Slide counter-clockwise to grab borrowed chords (♭III, ♭VI, ♭VII) for parallel-minor colour.",
            "Hear how plagal (IV→I) and authentic (V→I) cadences sound, and play either at any tempo.",
          ].map((t) => (
            <li key={t} className="flex items-start gap-3">
              <span className="mt-1 inline-block h-2 w-2 flex-shrink-0 rounded-full bg-amber-400" />
              <span className="text-nsm-ink">{t}</span>
            </li>
          ))}
        </ul>
      </Section>

      <Footer />

      <AnimatePresence>
        {playing && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 12 }}
            className="fixed bottom-4 left-1/2 -translate-x-1/2 rounded-full border border-amber-500/60 bg-nsm-bg/95 px-4 py-2 text-xs font-semibold text-amber-300 shadow-lg backdrop-blur"
          >
            ● Playing… click stop on Transport to interrupt
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
